import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deleteRequest, downloadBlobGet, fetchTextGet, getRequest } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Avatar } from 'primereact/avatar';
import { DateTime } from 'luxon';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import {
  AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, SearchIcon, ExportIcon, VerticalLineIcon,
  BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon,
  RefreshIcon,
} from '../../svg/svgIcons';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { LocaleContext } from '../../../components/LocaleContext';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../../utils/tabulatorScroll';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse, sanitizeTabulatorLayoutAttributes } from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../../utils/tabulatorLayoutUtils';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { dateUserHeaderFilter } from '../../../utils/dateUserHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth, TABULATOR_LIST_ID_MIN_WIDTH } from '../../../utils/tabulatorColumnMinWidth';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import TabulatorMaskDialog from '../../../utils/tabulatorMaskDialog';
import { applyTabulatorRuntimeI18n } from '../../../utils/tabulatorRuntimeI18n';

const SERVICE_STATE_COLUMN_I18N_KEYS = Object.freeze({
  id: 'ServiceTaskTable.Col.Id',
  name: 'ServiceStateAdd.StateName',
  applicable_types: 'ServiceStateAdd.ShowIn',
  color: 'ServiceStateAdd.StateColor',
  phase: 'ServiceStateAdd.Phase',
  transition_type: 'ServiceStateAdd.TransitionType',
  created_at: 'ServiceTaskTable.Col.Created',
  updated_at: 'ServiceTaskTable.Col.Updated',
});

/** Mimo komponentu – stabilné referencie pre headerFilterFunc / useCallback. */
const SERVICE_STATE_PHASES = [
  { name: 'ServiceState.Phase.Created', code: 'created' },
  { name: 'ServiceState.Phase.Approval', code: 'approval' },
  { name: 'ServiceState.Phase.SystemApproval', code: 'system_approval' },
  { name: 'ServiceState.Phase.Execution', code: 'execution' },
  { name: 'ServiceState.Phase.SystemExecution', code: 'system_execution' },
  { name: 'ServiceState.Phase.Inspection', code: 'inspection' },
  { name: 'ServiceState.Phase.SystemInspection', code: 'system_inspection' },
  { name: 'ServiceState.Phase.Closing', code: 'closing' },
];

const SERVICE_STATE_TRANSITION_TYPES = [
  { name: 'ServiceState.TransitionType.NonFinal', code: 'non_final' },
  { name: 'ServiceState.TransitionType.LevelFinal', code: 'level_final' },
  { name: 'ServiceState.TransitionType.SectionFinal', code: 'section_final' },
  { name: 'ServiceState.TransitionType.FinalManually', code: 'final_manually' },
  { name: 'ServiceState.TransitionType.FinalAuto', code: 'final_auto' },
  { name: 'ServiceState.TransitionType.Reopen', code: 'reopen' },
];

const DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE = Object.freeze({
  id: true,
  name: true,
  applicable_types: true,
  color: true,
  phase: true,
  transition_type: true,
  created_at: true,
  updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE };
  return merged;
}

function normalizeServiceStateColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE)) {
    out[field] = stateObj?.[field] !== false;
  }
  return out;
}

function columnsFromTabulatorForStandardMaskSave(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE)) {
    out[field] = raw?.[field] !== false;
  }
  return out;
}

function ensureServiceStateMaskDialogState(partial) {
  return mergeVisibility(partial);
}

function countServiceStateDataColumnsVisible(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE).filter((k) => vis[k] !== false).length;
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try {
    table.setHeaderFilterValue?.(field, '', true);
  } catch (_) {}
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

export default function ServiceStateList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const {
    setDialogText, setVisible, openFavoriteDialog, toast, setFastView, setUserMaskFilter, setStandardMaskFilter,
    getUserInitials, getUserColor, getDateFormat, formatDate, openChosenNotificationPreferences, getSlovakColorName,
  } = useContext(UtilContext);
  const { t, locale } = useContext(LocaleContext);

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const isRestoringRef = useRef(false);
  const submittedSearchValueRef = useRef('');

  const [editIcon, setEditIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchIconColor, setSearchIconColor] = useState('');
  const [maskVisible, setMaskVisible] = useState(false);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE });
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === 'ServiceStateList');
    if (!row) return false;
    const attributes = sanitizeTabulatorLayoutAttributes(safeParse(row.attributes, []));
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeVisibility(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;

  const reloadStates = useCallback(() => {
    getRequest('/api/service_states').then((r) => {
      dispatch({ type: 'load_service_states', newServiceStates: r.data });
    });
  }, [dispatch]);

  const parseHeaderFiltersToParams = useCallback(() => {
    const table = getTable();
    const hf = table?.getHeaderFilters?.() ?? [];
    const out = {};
    hf.forEach((f) => {
      if (!f?.field) return;
      const v = f.value;
      if (v == null || String(v).trim() === '') return;
      out[`filter_${f.field}`] = v;
    });
    return out;
  }, [getTable]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    try {
      table.getColumns().forEach((col) => {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field)) return;
        if (columnsVisible[field] === false) return;
        if (seen.has(field)) return;
        seen.add(field);
        out.push(field);
      });
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [getTable, columnsVisible]);

  const buildServiceStateExportQueryString = useCallback((opts = {}) => {
    const table = getTable();
    const params = {
      ...parseHeaderFiltersToParams(),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(table?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable, parseHeaderFiltersToParams]);

  const runServiceStatePrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildServiceStateExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/service_states/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), detail: t('Common.EnablePopups'), life: 4000 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Print'), detail: e?.message || t('ServiceTask.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildServiceStateExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, toast]);

  const exportServiceStateXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildServiceStateExportQueryString({
        includePrintColumns: true,
        printColumns: getPrintColumnsOrder(),
      });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.SERVICE_STATES));
      const { blob, filename } = await downloadBlobGet(`/api/service_states/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Export'), detail: e?.message || t('ServiceTask.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildServiceStateExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, toast]);

  const normalizeFilter = useCallback((headerValue, rowValue) => {
    if (headerValue == null || String(headerValue).trim() === '') return true;
    const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return norm(rowValue).includes(norm(headerValue));
  }, []);

  const typesHeaderFilter = useCallback((headerValue, rowValue, rowData) => {
    if (headerValue == null || String(headerValue).trim() === '') return true;
    const types = rowData?.applicable_types;
    if (!Array.isArray(types)) return false;
    const labels = types.map((code) => t(`ServiceTask.Type.${code}`));
    const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return norm(labels.join('/')).includes(norm(headerValue));
  }, [t]);

  const phaseHeaderFilter = useCallback((headerValue, rowValue, rowData) => {
    if (headerValue == null || String(headerValue).trim() === '') return true;
    const option = SERVICE_STATE_PHASES.find((item) => item.code === rowData?.phase);
    const display = option ? t(option.name) : '';
    const code = String(rowData?.phase ?? '');
    const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const needle = norm(headerValue);
    return norm(display).includes(needle) || norm(code).includes(needle);
  }, [t]);

  const transitionTypeHeaderFilter = useCallback((headerValue, rowValue, rowData) => {
    if (headerValue == null || String(headerValue).trim() === '') return true;
    const option = SERVICE_STATE_TRANSITION_TYPES.find((item) => item.code === rowData?.transition_type);
    const display = option ? t(option.name) : '';
    const code = String(rowData?.transition_type ?? '');
    const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const needle = norm(headerValue);
    return norm(display).includes(needle) || norm(code).includes(needle);
  }, [t]);

  const dateOrUserFilter = useCallback((headerValue, rowValue, rowData, filterParams) => {
    if (!headerValue) return true;
    const raw = String(headerValue || '').trim();
    if (raw.startsWith('range:')) {
      const p = raw.slice('range:'.length).split(':');
      if (p.length >= 2 && p[0] && p[1]) {
        const from = DateTime.fromFormat(p[0], 'yyyy-MM-dd').startOf('day');
        const to = DateTime.fromFormat(p[1], 'yyyy-MM-dd').endOf('day');
        const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
        return dt.isValid ? dt >= from && dt <= to : false;
      }
      return true;
    }
    const search = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const userObj = filterParams?.name === 'created_at'
      ? rowData?.created_by_user
      : rowData?.updated_by_user;
    const uname = userObj?.employee?.name || userObj?.username || '';
    const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
    const dateStr = dt.isValid ? `${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}` : '';
    const hay = `${uname} ${dateStr}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return hay.includes(search);
  }, [dateFmt, dateTimeFmt]);

  function TypeFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const types = rowData.applicable_types;
    if (!Array.isArray(types)) return null;
    const text = types.map((code) => t(`ServiceTask.Type.${code}`)).join('/');
    return (
      <span className="block w-full min-w-0 truncate whitespace-nowrap" title={text || undefined}>{text}</span>
    );
  }

  function NameCellFormatter(props) {
    const v = props.cell.getValue();
    const s = v != null ? String(v) : '';
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={s || undefined}>{s}</span>;
  }

  function PickedColorFormatter(props) {
    if (!props || !props.cell) return null;
    const raw = props.cell.getValue();
    const color = raw && !raw.startsWith('#') ? `#${raw}` : raw;
    if (!color) return null;
    return (
      <div
        style={{
          width: '23px',
          height: '23px',
          backgroundColor: color,
          borderRadius: '4px',
          border: '1px solid #ccc',
          margin: 'auto',
        }}
        title={getSlovakColorName ? getSlovakColorName(color) : color}
      />
    );
  }

  function PhaseFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const option = SERVICE_STATE_PHASES.find((item) => item.code === rowData?.phase);
    const text = option ? t(option.name) : '';
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={text || undefined}>{text}</span>;
  }

  function TransitionTypeFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const option = SERVICE_STATE_TRANSITION_TYPES.find((item) => item.code === rowData?.transition_type);
    const text = option ? t(option.name) : '';
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={text || undefined}>{text}</span>;
  }

  function onEditRow(service_state) {
    dispatch({ type: 'create_new_tab', name: t('ServiceStateList.EditStateTab'), translationKey: 'ServiceStateList.EditStateTab', legacyLabel: t('ServiceStateList.EditStateTab'), page: 'ServiceStateAdd', props: { service_state, type: 'edit' }, mask: 'intr007' });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}><EditRowIcon /></button>
      </div>
    );
  }

  function CreatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.created_by_user;
    const text = formatDate
      ? formatDate(rowData?.created_at, dateTimeFmt, true)
      : DateTime.fromISO(String(rowData?.created_at || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={u?.employee?.name || u?.username}
            label={getUserInitials(u?.employee?.name || u?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.updated_by_user;
    const text = formatDate
      ? formatDate(rowData?.updated_at, dateTimeFmt, true)
      : DateTime.fromISO(String(rowData?.updated_at || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={u?.employee?.name || u?.username}
            label={getUserInitials(u?.employee?.name || u?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  const columns = useMemo(() => ([
    {
      field: 'rowSelection',
      formatter: 'rowSelection',
      titleFormatter: 'rowSelection',
      hozAlign: 'center',
      width: 44,
      headerSort: false,
      resizable: false,
      frozen: true,
      print: false,
      download: false,
    },
    {
      title: '',
      field: 'editor',
      hozAlign: 'center',
      width: 44,
      headerSort: false,
      formatter: reactFormatter(<EditRowFormatter />),
      resizable: false,
      frozen: true,
      print: false,
      download: false,
    },
    {
      title: t('ServiceTaskTable.Col.Id'),
      field: 'id',
      minWidth: TABULATOR_LIST_ID_MIN_WIDTH,
      widthGrow: 0.6,
      hozAlign: 'left',
      sorter: 'number',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: normalizeFilter,
    },
    {
      title: t('ServiceStateAdd.StateName'),
      field: 'name',
      hozAlign: 'left',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: normalizeFilter,
      formatter: reactFormatter(<NameCellFormatter />),
      minWidth: tabulatorColumnMinWidthFromTitle(t('ServiceStateList.Column.StateName')),
      widthGrow: 1,
    },
    {
      title: t('ServiceStateAdd.ShowIn'),
      field: 'applicable_types',
      hozAlign: 'left',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: typesHeaderFilter,
      formatter: reactFormatter(<TypeFormatter />),
      minWidth: tabulatorColumnMinWidthFromTitle(t('ServiceStateList.Column.ShowIn')),
      widthGrow: 1,
    },
    {
      title: t('ServiceStateAdd.StateColor'),
      field: 'color',
      hozAlign: 'center',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: normalizeFilter,
      minWidth: tabulatorColumnMinWidthFromTitle(t('ServiceStateList.Column.Color')),
      widthGrow: 0.5,
      formatter: reactFormatter(<PickedColorFormatter />),
    },
    {
      title: t('ServiceStateAdd.Phase'),
      field: 'phase',
      hozAlign: 'left',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: phaseHeaderFilter,
      minWidth: tabulatorColumnMinWidthFromTitle(t('ServiceStateList.Column.Phase')),
      widthGrow: 1,
      formatter: reactFormatter(<PhaseFormatter />),
    },
    {
      title: t('ServiceStateAdd.TransitionType'),
      field: 'transition_type',
      hozAlign: 'left',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: transitionTypeHeaderFilter,
      minWidth: tabulatorColumnMinWidthFromTitle(t('ServiceStateList.Column.TransitionType')),
      widthGrow: 1,
      formatter: reactFormatter(<TransitionTypeFormatter />),
    },
    {
      title: t('ServiceTaskTable.Col.Created'),
      field: 'created_at',
      hozAlign: 'left',
      minWidth: tabulatorDateUserColumnMinWidth(t('ServiceStateList.Column.Created')),
      widthGrow: 1,
      formatter: reactFormatter(<CreatedAtCellFormatter />),
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: dateOrUserFilter,
      headerFilterFuncParams: { name: 'created_at' },
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
    },
    {
      title: t('ServiceTaskTable.Col.Updated'),
      field: 'updated_at',
      hozAlign: 'left',
      minWidth: tabulatorDateUserColumnMinWidth(t('ServiceStateList.Column.Updated')),
      widthGrow: 1,
      formatter: reactFormatter(<UpdatedAtCellFormatter />),
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: dateOrUserFilter,
      headerFilterFuncParams: { name: 'updated_at' },
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
    },
  ]), [
    dateFmt,
    dateTimeFmt,
    normalizeFilter,
    typesHeaderFilter,
    phaseHeaderFilter,
    transitionTypeHeaderFilter,
    dateOrUserFilter,
    t,
  ]);

  useEffect(() => {
    const table = getTable();
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: SERVICE_STATE_COLUMN_I18N_KEYS,
      placeholder: t('Common.NoData'),
      reformatRows: true,
    });
  }, [locale, t, getTable]);

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const openMaskDialog = () => {
    const merged = mergeVisibility(columnsVisible);
    setColumnsVisible(ensureServiceStateMaskDialogState(merged));
    setMaskVisible(true);
    closeMenu();
  };

  const saveUserMaskColumns = () => {
    const normalized = normalizeServiceStateColumnVisibilityForSave(columnsVisible);
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Mask'), detail: t('ServiceStateList.SelectAtLeastOneColumn'), life: 4000 });
      return;
    }
    const filtersSnapshot = getTable()?.getHeaderFilters?.() ?? [];
    setUserMaskFilter('ServiceStateList', 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('ServiceTask.SaveStandardMaskPrompt'),
      header: t('Common.SaveStandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) {
          toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Mask'), detail: t('ServiceStateList.TableNotReady'), life: 4000 });
          return;
        }
        const columnsForSave = columnsFromTabulatorForStandardMaskSave(layout.columns);
        if (!Object.values(columnsForSave).some(Boolean)) {
          toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceStateList.AtLeastOneColumnVisible'), life: 4000 });
          return;
        }
        setStandardMaskFilter('ServiceStateList', columnsForSave, layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === 'ServiceStateList');
    if (!standard) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.NoStandardMaskSaved'), life: 4000 });
      return;
    }
    const attributes = sanitizeTabulatorLayoutAttributes(safeParse(standard.attributes, []));
    if (!Array.isArray(attributes) || attributes.length <= 1) {
      toast?.current?.show({
        severity: 'warn',
        summary: t('ServiceTask.StandardMask'),
        detail: t('ServiceTask.StandardMaskInvalidLayout'),
        life: 5000,
      });
      return;
    }
    const jsonColumns = safeParse(standard.columns, {});
    const mergedCols = mergeVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizeServiceStateColumnVisibilityForSave(mergedCols);
    if (!Object.values(colsForSave).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceStateList.NoVisibleColumnInStandardMask'), life: 4000 });
      return;
    }
    confirmDialog({
      message: t('ServiceTask.ApplyStandardMaskPrompt'),
      header: t('ServiceTask.StandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Use'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter('ServiceStateList', 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          try {
            table.setColumnLayout?.(attributes);
          } catch (_) {}
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTable(table, colsForSave);
          setTimeout(() => {
            isRestoringRef.current = false;
            try {
              table.redraw?.(true);
            } catch (_) {}
          }, 0);
        });
      },
    });
  };

  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE, field)) return;
    const table = getTable();
    const next = { ...DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE, ...columnsVisible, [field]: value };
    if (value === false && countServiceStateDataColumnsVisible(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Mask'), detail: t('ServiceTask.AtLeastOneColumnMustRemainVisible'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE };
    for (const k of Object.keys(next)) next[k] = true;
    const table = getTable();
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    for (const k of Object.keys(DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE)) next[k] = false;
    next.id = true;
    const table = getTable();
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = [{
    key: '0',
    label: t('ServiceStateList.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('ServiceTask.SetFilter'), icon: 'pi pi-filter', command: () => { setUserMaskFilter('ServiceStateList', 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('ServiceTask.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { setUserMaskFilter('ServiceStateList', 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); closeMenu(); }, template: itemRenderer },
      { key: '0_3', label: t('ServiceTask.SetSort'), icon: 'pi pi-sort-alt', command: () => { setUserMaskFilter('ServiceStateList', 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); closeMenu(); }, template: itemRenderer },
      { key: '0_4', label: t('ServiceTask.ClearSort'), icon: 'pi pi-sort-alt-slash', command: () => { setUserMaskFilter('ServiceStateList', 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); closeMenu(); }, template: itemRenderer },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: openMaskDialog, template: itemRenderer },
      ...(hasApplicableStandardMask
        ? [{
          key: '0_5s',
          label: t('ServiceTask.StandardMask'),
          icon: 'pi pi-clone',
          command: confirmApplyStandardLayoutToUserMask,
          template: itemRenderer,
        }]
        : []),
      {
        key: '0_6',
        label: t('ServiceTask.Notifications'),
        icon: 'pi pi-envelope',
        command: () => { openChosenNotificationPreferences('ServiceStateList'); closeMenu(); },
        template: itemRenderer,
      },
    ],
  }];

  const handleClickOutside = (event) => {
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    const table = getTable();
    if (!table) return;
    if (normalized === '') {
      table.clearFilter(true);
    } else {
      const norm = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const needle = norm(normalized);
      table.setFilter((data) => norm(JSON.stringify(data)).includes(needle));
    }
    setVisibleSearch(false);
  };

  const onSelectRow = useCallback((data) => {
    const count = Array.isArray(data) ? data.length : 0;
    const canEdit = user?.permissions?.includes('edit service_task-setup');
    const canAdd = user?.permissions?.includes('add service_task-setup');
    const canDelete = user?.permissions?.includes('delete service_task-setup');
    const hasNonDeletable = Array.isArray(data) && data.some((row) => row.is_deletable === 0);
    setEditIcon(!canEdit || count !== 1);
    setCopyIcon(!canAdd || count !== 1);
    setDeleteIcon(!canDelete || count < 1 || hasNonDeletable);
  }, [user?.permissions]);

  const deleteSelected = useCallback(() => {
    const table = getTable();
    const selected = table?.getSelectedData?.() ?? [];
    Promise.all(selected.map((x) => deleteRequest(`/api/service_states/${x.id}`)))
      .then(() => {
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('ServiceTask.RecordDeleted'), life: 3000 });
        reloadStates();
      })
      .catch((e) => {
        toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: e?.message || t('ServiceTask.DeleteFailed'), life: 3000 });
      });
  }, [getTable, reloadStates, toast]);

  const confirmDelete = useCallback(() => {
    confirmDialog({
      message: t('ServiceTask.ConfirmDeleteRecord'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: deleteSelected,
    });
  }, [deleteSelected]);

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="add"><button type="button" onClick={() => dispatch({ type: 'create_new_tab', name: t('ServiceStateAdd.NewState'), translationKey: 'ServiceStateAdd.NewState', legacyLabel: t('ServiceStateAdd.NewState'), page: 'ServiceStateAdd', props: { service_state: null, type: 'add' }, mask: 'intr007' })}><AddIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="edit"><button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={() => dispatch({ type: 'create_new_tab', name: t('ServiceStateList.EditStateTab'), translationKey: 'ServiceStateList.EditStateTab', legacyLabel: t('ServiceStateList.EditStateTab'), page: 'ServiceStateAdd', props: { service_state: getTable()?.getSelectedData?.()?.[0], type: 'edit' }, mask: 'intr007' })}><EditIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="copy"><button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={() => dispatch({ type: 'create_new_tab', name: t('ServiceStateAdd.NewState'), translationKey: 'ServiceStateAdd.NewState', legacyLabel: t('ServiceStateAdd.NewState'), page: 'ServiceStateAdd', props: { service_state: getTable()?.getSelectedData?.()?.[0], type: 'add' }, mask: 'intr007' })}><CopyIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="delete"><button type="button" className="disabled:opacity-25" disabled={deleteIcon} onClick={confirmDelete}><DeleteIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="print"><button type="button" title={t('Common.Print')} onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button
              type="button"
              title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')}
              onClick={
                searchIconColor !== 'active'
                  ? () => setVisibleSearch(true)
                  : () => {
                      setSearchIconColor('');
                      setSearchValue('');
                      submittedSearchValueRef.current = '';
                      const table = getTable();
                      table?.clearFilter?.(true);
                    }
              }
            >
              {searchIconColor === '' ? (
                <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />
              ) : (
                <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />
              )}
            </button>
          </div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="export"><button type="button" title={t('Common.ExportToExcel')} onClick={exportServiceStateXlsx}><ExportIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh"><button type="button" title={t('Common.Refresh')} onClick={reloadStates}><RefreshIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first"><button type="button" onClick={scrollToFirst} title={t('Icon.First')}><BackToFirstIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back"><button type="button" onClick={scrollUp} title={t('Icon.Previous')}><BackIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward"><button type="button" onClick={scrollDown} title={t('Icon.Next')}><ForwardIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last"><button type="button" onClick={scrollToLast} title={t('Icon.Last')}><ForwardToLastIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite"><button type="button" onClick={() => openFavoriteDialog(t('ServiceStateList.Title'), 'ServiceStateList')}><FavoriteIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="price"><button type="button" onClick={() => setFastView('ServiceStateList', columnsVisible, columns)}><PriceIcon /></button></div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('ServiceTask.SaveStandardMaskTitle')}
                aria-label={t('ServiceTask.SaveStandardMaskTitle')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>

      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <ReactTabulator
                key="service-state-list"
                data={state.service_states}
                className="h-[calc(100vh_-_193px)]"
                onRef={(r) => { ref.current = r?.current; }}
                columns={columns}
                events={{
                  rowSelectionChanged: onSelectRow,
                  tableBuilt: () => {
                    const table = getTable();
                    if (!table) return;
                    const effective = getEffectiveMask('ServiceStateList', state);
                    if (effective?.type === 'user' && effective.userMask) {
                      const m = effective.userMask;
                      isRestoringRef.current = true;
                      applyUserMaskRowToTabulator(table, m, {
                        setColumnsVisible: (v) => setColumnsVisible(mergeVisibility(v)),
                        clearHeaderFiltersWhenEmpty: true,
                        applyColumnVisibilityFn: (tbl, jc, setVis) => {
                          const merged = mergeVisibility(jc);
                          setVis(merged);
                          applyDataColumnVisibilityToTable(tbl, merged);
                        },
                      });
                      {
                        const merged = mergeVisibility(safeParse(m.columns, {}));
                        for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE)) {
                          if (merged[field] === false) clearHeaderFilterForField(table, field);
                        }
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else if (effective?.type === 'standard' && effective.standardMask) {
                      const m = effective.standardMask;
                      isRestoringRef.current = true;
                      const attributes = sanitizeTabulatorLayoutAttributes(safeParse(m.attributes, []));
                      const json_columns = safeParse(m.columns, {});
                      if (Array.isArray(attributes) && attributes.length > 1) {
                        table.setColumnLayout?.(attributes);
                      }
                      if (json_columns && typeof json_columns === 'object') {
                        const merged = mergeVisibility(json_columns);
                        setColumnsVisible(merged);
                        applyDataColumnVisibilityToTable(table, merged);
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    }
                  },
                  ...getTabulatorPersistLayoutEvents('ServiceStateList', ref, setUserMaskFilter),
                }}
                options={{
                  layout: 'fitColumns',
                  renderHorizontal: 'basic',
                  movableColumns: true,
                  autoResize: true,
                  headerSortTristate: true,
                  filterMode: 'local',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={runServiceStatePrint}
      />

      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('Common.GlobalSearchHint')}</p>
          <div className="flex items-center gap-3 w-full">
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              type="text"
              className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400"
            />
            <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center">
              <SearchIcon />
            </button>
          </div>
        </div>
      </Dialog>

      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('Common.SetMask')}
        fields={Object.keys(DEFAULT_COLUMNS_VISIBLE_SERVICE_STATE)}
        checkedMap={columnsVisible}
        onToggleField={setMask}
        onSelectAll={maskSelectAll}
        onDeselectAll={maskDeselectAll}
        onSave={saveUserMaskColumns}
        selectAllLabel={t('Common.SelectAll')}
        deselectAllLabel={t('ServiceStateList.DeselectAllExceptId')}
        saveLabel={t('Common.Save')}
        getFieldLabel={(field) => {
          const col = columns.find((item) => item?.field === field);
          return col?.title || field;
        }}
        inputIdPrefix="ss-mask"
      />
    </>
  );
}
